import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import {
  validateModerationReport,
  type ModerationReportReason,
  type ModerationReportReceipt,
  type ModerationTargetType
} from "@puntopy/contracts";
import { postJson } from "./api-client.js";
import { Feedback, Icon } from "./MarketplaceUI.js";

const BLOCKED_BUSINESSES_KEY = "puntopy:blocked-businesses:v1";
const BLOCKED_BUSINESSES_EVENT = "puntopy:blocked-businesses-changed";

export interface BlockedBusiness {
  id: string;
  name: string;
}

interface ReportTarget {
  targetType: ModerationTargetType;
  businessId: string;
  productId: string | null;
  label: string;
}

function readBlockedBusinesses(): BlockedBusiness[] {
  if (typeof window === "undefined") return [];
  try {
    const parsed = JSON.parse(window.localStorage.getItem(BLOCKED_BUSINESSES_KEY) ?? "[]") as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((item): item is BlockedBusiness => typeof item === "object" && item !== null
      && typeof (item as BlockedBusiness).id === "string" && typeof (item as BlockedBusiness).name === "string").slice(0, 500);
  } catch { return []; }
}

function saveBlockedBusinesses(items: BlockedBusiness[]): void {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(BLOCKED_BUSINESSES_KEY, JSON.stringify(items.slice(0, 500)));
  window.dispatchEvent(new Event(BLOCKED_BUSINESSES_EVENT));
}

export function blockBusiness(business: BlockedBusiness): void {
  const current = readBlockedBusinesses().filter((item) => item.id !== business.id);
  saveBlockedBusinesses([{ id: business.id, name: business.name.slice(0, 120) }, ...current]);
}

export function unblockBusiness(id: string): void {
  saveBlockedBusinesses(readBlockedBusinesses().filter((item) => item.id !== id));
}

export function clearBlockedBusinesses(): void {
  saveBlockedBusinesses([]);
}

export function useBlockedBusinesses(): BlockedBusiness[] {
  const [blocked, setBlocked] = useState<BlockedBusiness[]>(readBlockedBusinesses);
  useEffect(() => {
    const refresh = () => setBlocked(readBlockedBusinesses());
    window.addEventListener(BLOCKED_BUSINESSES_EVENT, refresh);
    window.addEventListener("storage", refresh);
    return () => {
      window.removeEventListener(BLOCKED_BUSINESSES_EVENT, refresh);
      window.removeEventListener("storage", refresh);
    };
  }, []);
  return blocked;
}

const reasonLabels: Record<ModerationReportReason, string> = {
  MISLEADING: "Información engañosa o falsa",
  PROHIBITED: "Producto o actividad prohibida",
  INAPPROPRIATE: "Contenido ofensivo o inapropiado",
  INFRINGEMENT: "Uso no autorizado de marca, imagen o propiedad intelectual",
  PERSONAL_DATA: "Datos personales publicados sin autorización",
  OTHER: "Otro motivo"
};

export function ReportContentButton({ target, className = "mp-safety-button" }: { target: ReportTarget; className?: string }) {
  const dialog = useRef<HTMLDialogElement>(null);
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState<ModerationReportReason>("MISLEADING");
  const [details, setDetails] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [receipt, setReceipt] = useState<ModerationReportReceipt | null>(null);
  useEffect(() => {
    if (open && !dialog.current?.open) dialog.current?.showModal();
    if (!open && dialog.current?.open) dialog.current.close();
  }, [open]);
  const close = () => { if (!busy) { setOpen(false); setError(null); setReceipt(null); } };
  const submit = async (event: FormEvent) => {
    event.preventDefault(); setError(null);
    const validation = validateModerationReport({
      targetType: target.targetType,
      businessId: target.businessId,
      productId: target.productId,
      reason,
      details
    });
    if (!validation.ok) { setError(validation.error); return; }
    setBusy(true);
    try {
      setReceipt(await postJson<ModerationReportReceipt>("/v1/moderation/reports", validation.value));
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : "No se pudo enviar el reporte.");
    } finally { setBusy(false); }
  };
  return <>
    <button className={className} type="button" onClick={() => { setOpen(true); setError(null); setReceipt(null); }}><Icon name="shield" />Reportar</button>
    <dialog ref={dialog} className="mp-report-dialog" aria-labelledby={`report-title-${target.businessId}-${target.productId ?? "business"}`} onCancel={(event) => { event.preventDefault(); close(); }}>
      <div className="mp-report-dialog-heading"><div><p className="section-kicker">Seguridad de la comunidad</p><h2 id={`report-title-${target.businessId}-${target.productId ?? "business"}`}>Reportar {target.label}</h2></div><button className="mp-icon-button" type="button" aria-label="Cerrar reporte" disabled={busy} onClick={close}><Icon name="close" /></button></div>
      {receipt ? <div className="mp-report-success"><span><Icon name="check" /></span><h3>Recibimos tu reporte</h3><p>La administración podrá revisar el contenido y aplicar la medida correspondiente. No vuelvas a enviarlo por el mismo motivo.</p><small>Referencia: {receipt.id}</small><button className="mp-button primary" type="button" onClick={close}>Cerrar</button></div> : <form onSubmit={(event) => void submit(event)}>
        <p>Elegí el motivo que mejor describa el problema. Los reportes falsos o abusivos también pueden revisarse.</p>
        <label className="mp-field"><span>Motivo</span><select value={reason} onChange={(event) => setReason(event.target.value as ModerationReportReason)}>{Object.entries(reasonLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
        <label className="mp-field"><span>Detalle {reason === "OTHER" ? "obligatorio" : "opcional"}</span><textarea rows={4} maxLength={1000} minLength={reason === "OTHER" ? 8 : undefined} required={reason === "OTHER"} value={details} placeholder="Explicá qué observaste sin incluir contraseñas, documentos completos ni datos sensibles innecesarios." onChange={(event) => setDetails(event.target.value)} /><small>{details.length}/1000</small></label>
        <Feedback error={error} />
        <p className="mp-report-policy">Al enviar aceptás que PuntoPY use estos datos para moderar la plataforma según las <a href="/terminos" target="_blank" rel="noopener noreferrer">condiciones</a> y la <a href="/privacidad" target="_blank" rel="noopener noreferrer">política de privacidad</a>.</p>
        <div className="mp-inline-actions"><button className="mp-button primary" type="submit" disabled={busy}>{busy ? "Enviando…" : "Enviar reporte"}</button><button className="mp-button outline" type="button" disabled={busy} onClick={close}>Cancelar</button></div>
      </form>}
    </dialog>
  </>;
}

export function BlockBusinessButton({ business, className = "mp-safety-button", onChange }: { business: BlockedBusiness; className?: string; onChange?: (blocked: boolean) => void }) {
  const blocked = useBlockedBusinesses();
  const isBlocked = useMemo(() => blocked.some((item) => item.id === business.id), [blocked, business.id]);
  const toggle = () => {
    if (isBlocked) {
      unblockBusiness(business.id); onChange?.(false); return;
    }
    if (!window.confirm(`¿Ocultar “${business.name}” y sus productos en este dispositivo? Podés volver a mostrarlos cuando quieras.`)) return;
    blockBusiness(business); onChange?.(true);
  };
  return <button className={className} type="button" onClick={toggle}><Icon name={isBlocked ? "check" : "close"} />{isBlocked ? "Mostrar comercio" : "Ocultar comercio"}</button>;
}
